import * as v from 'valibot'
import {
  I2vProInnerEntries,
  ReferenceVideoInnerEntries,
} from 'src/services/helpers/schema/taskParameters'

/**
 * GraphQL's `TypedParameters` scalar surfaces missing fields as `null` rather
 * than `undefined`, so the import parser must accept both. Wrap each canonical
 * inner entry in `v.nullish(...)` so the import shape mirrors the server-facing
 * shape exactly — adding a field to the canonical entries (see
 * `services/helpers/schema/taskParameters.ts`) propagates here for free.
 */
function nullishifyEntries<
  E extends Record<string, v.GenericSchema<unknown, unknown>>,
>(entries: E): { [K in keyof E]: v.NullishSchema<E[K], undefined> } {
  const out = {} as { [K in keyof E]: v.NullishSchema<E[K], undefined> }
  for (const key of Object.keys(entries) as Array<keyof E>) {
    out[key] = v.nullish(entries[key]) as (typeof out)[typeof key]
  }
  return out
}

const I2vProImportEntries = nullishifyEntries(I2vProInnerEntries)
const ReferenceVideoImportEntries = nullishifyEntries(
  ReferenceVideoInnerEntries,
)

/**
 * Field-key lists derived from the canonical entries. Consumers iterate these
 * to merge parsed task parameters into form state without hand-listing each
 * field — adding a new entry in the canonical schema is enough to make the
 * field round-trip on import (subject to type/coercion mismatches, which the
 * caller still handles explicitly).
 */
const I2V_PRO_FIELDS = Object.keys(I2vProInnerEntries) as Array<
  keyof typeof I2vProInnerEntries
>
const REFERENCE_VIDEO_FIELDS = Object.keys(ReferenceVideoInnerEntries) as Array<
  keyof typeof ReferenceVideoInnerEntries
>

/**
 * Fields safe to merge from a parsed i2vPro payload into the form state.
 * Excludes `mediaId`, which the form treats as a separate "start frame"
 * input held outside the form atom (see `I2VProFormArgs` `Omit` in `types.ts`).
 */
export const I2V_PRO_FORM_FIELDS = I2V_PRO_FIELDS.filter(
  (k): k is Exclude<keyof typeof I2vProInnerEntries, 'mediaId'> =>
    k !== 'mediaId',
)

/**
 * Fields on `referenceVideo` that diverge from the form atom and require
 * explicit handling at the call site (rename `prompt` → `prompts`, coerce
 * numeric `duration` to string, route `reference*MediaIds` into
 * `multiRefResource`). Listed as the **exclusion** set so the matching
 * inclusion list ({@link REFERENCE_VIDEO_FORM_FIELDS}) auto-grows when a new
 * 1:1 field is added to {@link ReferenceVideoInnerEntries}.
 */
const REFERENCE_VIDEO_NON_FORM_FIELDS = [
  'prompt',
  'duration',
  'referenceImageMediaIds',
  'referenceVideoMediaIds',
  'referenceAudioMediaIds',
] as const satisfies ReadonlyArray<keyof typeof ReferenceVideoInnerEntries>

type ReferenceVideoNonFormField =
  (typeof REFERENCE_VIDEO_NON_FORM_FIELDS)[number]

/**
 * Fields safe to merge 1:1 from a parsed `referenceVideo` payload into the
 * i2v form state via {@link assignDefined}. Derived from the canonical
 * `ReferenceVideoInnerEntries` minus the rename/coerce/route-elsewhere
 * exclusions, so adding a new field to the canonical entries propagates here
 * without further edits.
 */
export const REFERENCE_VIDEO_FORM_FIELDS = REFERENCE_VIDEO_FIELDS.filter(
  (
    k,
  ): k is Exclude<
    keyof typeof ReferenceVideoInnerEntries,
    ReferenceVideoNonFormField
  > => !(REFERENCE_VIDEO_NON_FORM_FIELDS as readonly string[]).includes(k),
)

const I2vTaskParametersImportSchema = v.looseObject({
  width: v.nullish(v.number()),
  height: v.nullish(v.number()),
  priority: v.nullish(v.number()),
  mediaId: v.nullish(v.string()),
  tailMediaId: v.nullish(v.string()),
  sceneId: v.nullish(v.string()),
  channel: v.nullish(v.picklist(['normal', 'private'])),
  lora: v.nullish(v.record(v.string(), v.number())),
  extra: v.nullish(
    v.looseObject({
      naturalPrompts: v.nullish(v.string()),
      referenceImagePrompts: v.nullish(v.string()),
    }),
  ),
  i2vPro: v.nullish(v.looseObject(I2vProImportEntries)),
  referenceVideo: v.nullish(v.looseObject(ReferenceVideoImportEntries)),
})

export type I2vTaskParametersPartial = v.InferOutput<
  typeof I2vTaskParametersImportSchema
>

export function parseI2vTaskParameters(
  input: unknown,
): I2vTaskParametersPartial {
  const result = v.safeParse(
    I2vTaskParametersImportSchema,
    input ?? Object.create(null),
  )
  return result.success ? result.output : {}
}
